import React from "react";
import { Box, Chip, Button, Stack, Typography } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  MANAGER_COLORS,
  emptyStateSx,
  versionCardSx,
  outlinedButtonSx,
  primaryButtonSx,
  helperCaptionSx,
} from "./managerTheme.js";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const VersionHistoryPanel = ({ versions, activeVersionId, onRestore, onPublish, onDelete }) => {
  if (!versions?.length) {
    return (
      <Box sx={emptyStateSx}>
        <HistoryOutlinedIcon sx={{ fontSize: 40, color: MANAGER_COLORS.border, mb: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          No saved versions yet
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Save a draft to create the first version for this doctor.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <HistoryOutlinedIcon sx={{ fontSize: 18, color: MANAGER_COLORS.primary }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
          Version history
        </Typography>
        <Chip
          size="small"
          label={`${versions.length} version${versions.length === 1 ? "" : "s"}`}
          sx={{ height: 22, fontSize: "0.7rem", bgcolor: MANAGER_COLORS.accentLight }}
        />
      </Stack>
      <Typography sx={{ ...helperCaptionSx, mb: 2 }}>
        Load a previous layout into the draft editor, publish an older version, or delete versions
        you no longer need.
      </Typography>

      {versions.map((version) => {
        const isActive = version.id === activeVersionId;
        const isDraft = !version.publishedAt;

        return (
          <Box key={version.id} sx={versionCardSx(isActive)}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={2}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}>
                    {version.label}
                  </Typography>
                  {isActive && (
                    <Chip size="small" color="success" label="Active" sx={{ height: 22, fontWeight: 600 }} />
                  )}
                  {isDraft && !isActive && (
                    <Chip
                      size="small"
                      label="Draft"
                      variant="outlined"
                      sx={{ height: 22, borderColor: MANAGER_COLORS.border }}
                    />
                  )}
                  {version.publishedAt && !isActive && (
                    <Chip
                      size="small"
                      label="Previously published"
                      variant="outlined"
                      sx={{ height: 22, borderColor: MANAGER_COLORS.border }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  Saved {formatDate(version.createdAt)}
                  {version.publishedAt ? ` · Published ${formatDate(version.publishedAt)}` : ""}
                </Typography>
              </Box>

              <Stack direction="row" gap={1} flexShrink={0}>
                <Button
                  size="small"
                  startIcon={<RestoreOutlinedIcon />}
                  onClick={() => onRestore(version)}
                  sx={outlinedButtonSx}
                >
                  Load into Draft
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={isActive ? null : <PublishOutlinedIcon />}
                  disabled={isActive}
                  onClick={() => onPublish(version.id)}
                  sx={primaryButtonSx}
                >
                  {isActive ? "Published" : "Publish"}
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={() => {
                    const message = isActive
                      ? `Delete "${version.label}"? This is the active published version — Doctor Console will fall back to the app default until another version is published.`
                      : `Delete "${version.label}"? This cannot be undone.`;
                    if (window.confirm(message)) {
                      onDelete(version.id);
                    }
                  }}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default VersionHistoryPanel;
